import { IActiveCountry, IActiveGroup } from 'services/APIService';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    map,
    find,
    filter,
    includes,
    toLower,
    get,
    size,
    isEmpty,
    flow,
    keyBy,
    trim,
} from 'lodash/fp';
import CountryPickerListItem from './CountryPickerListItem';
import CountryPickerSelectAllListItem from './CountryPickerSelectAllListItem';
import GroupSection from 'components/GQCountryPicker/GQcountryPickerGroupsSection';
import { GQButton } from 'components/GQButton';
import { sortCountriesByName } from 'utils/countryUtils';
import cx from 'classnames';
import { FGStack, FGTile } from '@fg/design-system';

type HorizontalAlignment = 'left' | 'right';

export type CountryPickerProps = {
    isMultiSelect: boolean;
    countriesList: IActiveCountry[];
    selectedCountriesList: number[];
    groups?: IActiveGroup[];
    activeGroupId?: string;
    onGroupClicked?: (id: string, scroll: number) => void;
    onCountrySelected: (id: number) => void;
    onAllSelected?: () => void;
    onAllUnselected?: () => void;
    canSelectAllCountries: boolean;
    horizontalAlignment?: HorizontalAlignment;
    activeGroupScroll?: number;
};

//todo: delete this file and all old country picker files
const CountryPicker = (props: CountryPickerProps) => {
    const {
        activeGroupId,
        isMultiSelect,
        countriesList,
        selectedCountriesList,
        groups,
        onGroupClicked,
        onCountrySelected,
        canSelectAllCountries,
        horizontalAlignment = 'left',
        activeGroupScroll,
    } = props;
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

    useEffect(() => {
        setSearchKeyword('');
    }, [activeGroupId]);

    useEffect(() => {
        const activeCountryGroup = find(
            group => group.id === activeGroupId,
            groups
        );
        const activeCountryGroupCountries = filter(
            country =>
                !activeCountryGroup ||
                includes(country.id, get('countries', activeCountryGroup)),
            countriesList
        );

        setIsAllSelected(
            size(activeCountryGroupCountries) === size(selectedCountriesList)
        );
    }, [countriesList, selectedCountriesList, activeGroupId, groups]);

    const activeCountryGroup = useMemo(() => {
        return find(group => group.id === activeGroupId, groups);
    }, [groups, activeGroupId]);

    const searchKeywordLowerCase = trim(toLower(searchKeyword));
    const visibleCountries = useMemo((): IActiveCountry[] => {
        const countriesById: { [countryID: number]: IActiveCountry } = keyBy(
            (country: IActiveCountry) => country.id,
            countriesList
        );
        // When a group is active the list is rebuilt from `activeCountryGroup.countries`,
        // which is an array of country IDs in the group's stored (ID) order. Re-sort by
        // name so the picker is always alphabetically ordered, matching the top-level list.
        const countriesForActiveGroup = sortCountriesByName(
            activeCountryGroup
                ? flow(
                      get('countries'),
                      map((countryId: number) => get(countryId, countriesById)),
                      filter(Boolean)
                  )(activeCountryGroup)
                : countriesList
        );

        if (!searchKeywordLowerCase) {
            return countriesForActiveGroup;
        }

        return filter(
            country =>
                includes(searchKeywordLowerCase, toLower(get('name', country))),

            countriesForActiveGroup
        );
    }, [activeCountryGroup, searchKeywordLowerCase, countriesList]);

    const onSearchTextChange = useCallback(
        event => setSearchKeyword(event.target.value),
        []
    );

    const onAllItemsClick = () => {
        isAllSelected
            ? props.onAllUnselected && props.onAllUnselected()
            : props.onAllSelected && props.onAllSelected();
    };

    const onClearSearchClick = useCallback(() => setSearchKeyword(''), []);

    return (
        <div
            className={cx('new-country-picker-container', {
                'align-right': horizontalAlignment === 'right',
            })}>
            <FGTile padding="none" appearance="secondary">
                <FGStack direction="column" style={{ width: '100%' }}>
                    <GroupSection
                        groups={groups}
                        selectedGroup={activeGroupId}
                        onGroupClick={onGroupClicked}
                        activeGroupScroll={activeGroupScroll}
                    />
                    <div className="new-country-picker-search-and-list-container">
                        <div className="new-country-picker-search-container">
                            <i className="new-country-picker-search-icon gqi-search" />
                            <input
                                className="new-country-picker-search-input"
                                type="search"
                                placeholder="Search..."
                                value={searchKeyword}
                                onChange={onSearchTextChange}
                            />
                            {!isEmpty(searchKeyword) && (
                                <GQButton
                                    icon="gqi-clear"
                                    onClick={onClearSearchClick}
                                    noBorder
                                />
                            )}
                        </div>

                        <div className="new-country-picker-list-container">
                            {canSelectAllCountries && (
                                <div className="new-country-picker-select-all-container">
                                    <CountryPickerSelectAllListItem
                                        onClick={onAllItemsClick}
                                        isSelected={isAllSelected}
                                    />
                                </div>
                            )}
                            <ul className="new-country-picker-list">
                                {map((countryListItem: IActiveCountry) => {
                                    return (
                                        <li
                                            onClick={() => {
                                                onCountrySelected(
                                                    countryListItem.id
                                                );
                                            }}
                                            key={countryListItem.id}
                                            className="new-country-picker-list-item">
                                            <CountryPickerListItem
                                                isMultiSelect={isMultiSelect}
                                                id={countryListItem.id}
                                                name={countryListItem.name}
                                                isSelected={includes(
                                                    countryListItem.id,
                                                    selectedCountriesList
                                                )}
                                            />
                                        </li>
                                    );
                                }, visibleCountries)}
                            </ul>
                        </div>
                    </div>
                </FGStack>
            </FGTile>
        </div>
    );
};

export default CountryPicker;
